import { useEffect, useState } from 'react'
import type { Room as ColyseusRoom } from 'colyseus.js'
import type { Room, GamePhase } from '../types'

const VALID_PHASES: GamePhase[] = [
  'lobby',
  'discussion',
  'rankerRanking',
  'groupGuess',
  'individualGuess',
  'revealing',
  'lineReveal',
  'results',
]

function normalizePhase(phase: unknown): GamePhase {
  if (typeof phase === 'string' && (VALID_PHASES as string[]).includes(phase)) {
    return phase as GamePhase
  }
  return 'lobby'
}

function snapshotState(room: ColyseusRoom, code: string): Room {
  // Colyseus state is a Schema object; arrays/maps behave like the JS
  // collections but we copy to plain values so React sees stable references.
  const s = room.state as Record<string, unknown> & {
    hostId?: string
    phase?: string
    players?: { forEach: (cb: (p: { id: string; name: string; connected: boolean; departed: boolean }, key: string) => void) => void }
    bowl?: ArrayLike<string>
    currentRankerId?: string
    scribeId?: string
    drawnItems?: ArrayLike<string>
    rankerRanking?: ArrayLike<string>
    rankerLineIndex?: number
    groupRanking?: ArrayLike<string>
    groupLineIndex?: number
    individualRankings?: { forEach: (cb: (sub: { ranking: ArrayLike<string>; lineIndex: number }, key: string) => void) => void }
    individualSubmitted?: ArrayLike<string>
    revealedCount?: number
    lineRevealed?: boolean
    usedRankers?: ArrayLike<string>
    usedScribes?: ArrayLike<string>
    roundNumber?: number
    leaderboard?: { forEach: (cb: (v: number, k: string) => void) => void }
    gameMode?: string
    lastRoundScores?: { forEach: (cb: (sc: { sideMatches: number; exactMatches: number; total: number }, key: string) => void) => void }
    hostRequest?: string
    seatTakeoverRequester?: string
    seatTakeoverTarget?: string
    reactions?: { forEach: (cb: (r: { itemText: string; emoji: string; userId: string }) => void) => void }
  }

  const players: Room['players'] = {}
  s.players?.forEach((p, key) => {
    players[key] = { id: p.id, name: p.name, connected: !!p.connected, departed: !!p.departed }
  })

  const individualRankings: Room['individualRankings'] = {}
  s.individualRankings?.forEach((sub, key) => {
    individualRankings[key] = {
      ranking: Array.from(sub.ranking),
      lineIndex: sub.lineIndex,
    }
  })

  const leaderboard: Room['leaderboard'] = {}
  s.leaderboard?.forEach((v, k) => {
    leaderboard[k] = v
  })

  const lastRoundScores: Room['lastRoundScores'] = {}
  s.lastRoundScores?.forEach((sc, k) => {
    lastRoundScores[k] = {
      sideMatches: sc.sideMatches,
      exactMatches: sc.exactMatches,
      total: sc.total,
    }
  })

  const arr = (a: ArrayLike<string> | undefined): string[] => (a ? Array.from(a) : [])

  return {
    code,
    hostId: s.hostId ?? '',
    phase: normalizePhase(s.phase),
    gameMode: (s.gameMode === 'group' ? 'group' : 'individual'),
    players,
    bowl: arr(s.bowl),
    currentRankerId: s.currentRankerId ? s.currentRankerId : null,
    scribeId: s.scribeId ? s.scribeId : null,
    drawnItems: arr(s.drawnItems),
    rankerRanking: arr(s.rankerRanking),
    rankerLineIndex: s.rankerLineIndex ?? 0,
    groupRanking: arr(s.groupRanking),
    groupLineIndex: s.groupLineIndex ?? 0,
    individualRankings,
    individualSubmitted: arr(s.individualSubmitted),
    revealedCount: s.revealedCount ?? 0,
    lineRevealed: !!s.lineRevealed,
    usedRankers: arr(s.usedRankers),
    usedScribes: arr(s.usedScribes),
    roundNumber: s.roundNumber ?? 0,
    leaderboard,
    lastRoundScores,
    hostRequest: s.hostRequest ?? '',
    seatTakeoverRequester: s.seatTakeoverRequester ?? '',
    seatTakeoverTarget: s.seatTakeoverTarget ?? '',
    reactions: (() => {
      const out: Room['reactions'] = []
      s.reactions?.forEach((r) => {
        out.push({ itemText: r.itemText, emoji: r.emoji, userId: r.userId })
      })
      return out
    })(),
  }
}

export function useRoom(colyseusRoom: ColyseusRoom | null, code: string | null) {
  const [room, setRoom] = useState<Room | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!colyseusRoom || !code) {
      setRoom(null)
      return
    }

    setRoom(snapshotState(colyseusRoom, code))

    const onStateChange = (_state: unknown) => {
      setRoom(snapshotState(colyseusRoom, code))
    }
    const onError = (codeNum: number, message?: string) => {
      console.error('[room] error', codeNum, message)
      setError(message ?? 'connection error')
    }
    const onLeave = (leaveCode: number) => {
      console.log('[room] left', leaveCode)
      // Intentionally do NOT setRoom(null). App.tsx's wireRoom handles
      // the reconnect dance: it tries client.reconnect(savedToken) and
      // swaps colyseusRoom only on success or definitive failure. If we
      // null the snapshot here, App's `!room` guard sends the user to
      // Home mid-reconnect (the bug that made phone-screen-lock feel
      // like a logout). Keep the last good snapshot frozen until
      // colyseusRoom changes — which retriggers this effect's cleanup
      // and a fresh snapshot from the new Room.
    }

    colyseusRoom.onStateChange(onStateChange)
    colyseusRoom.onError(onError)
    colyseusRoom.onLeave(onLeave)

    return () => {
      colyseusRoom.onStateChange.remove(onStateChange)
      colyseusRoom.onError.remove(onError)
      colyseusRoom.onLeave.remove(onLeave)
    }
  }, [colyseusRoom, code])

  return { room, error }
}
